import { useEffect, useCallback, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CommanServices } from '@/services/commanServices';
import { readNotificationSocket } from '@/Redux/Action/socketActions';
import { readStoredUser } from '@/auth/saasSession';
import { isLicensedNotification } from '@/auth/featureEntitlements';

const isUnread = (n) => n?.read === false || n?.read === 0;

const parseNotificationsResponse = (res) => {
  if (Array.isArray(res)) return res;
  if (res?.status && Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const normalizeSocketNotification = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const n = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  if (!n || typeof n !== 'object') return null;
  if (!n.id && !n._id && !n.title) return null;
  return n;
};

const notificationKey = (n) => String(n?._id || n?.id || `${n?.title || ''}-${n?.created_at || ''}`);

const dedupeNotifications = (list) => {
  const seen = new Set();
  return list.filter((n) => {
    const key = notificationKey(n);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getUnreadCount = (notifications) => notifications.filter(isUnread).length;

const store = {
  notifications: [],
  loading: false,
  listeners: new Set(),
};

let snapshot = {
  notifications: [],
  loading: false,
  unreadCount: 0,
};

let lastProcessedSocketKey = null;
let fetchPromise = null;

const subscribe = (listener) => {
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
};

const updateSnapshot = () => {
  snapshot = {
    notifications: store.notifications,
    loading: store.loading,
    unreadCount: getUnreadCount(store.notifications),
  };
};

const getSnapshot = () => snapshot;

const notify = () => {
  updateSnapshot();
  store.listeners.forEach((listener) => listener());
};

const setStore = (patch) => {
  Object.assign(store, patch);
  notify();
};

const fetchNotifications = async () => {
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    setStore({ loading: true });
    try {
      const res = await CommanServices.getNotifications();
      const user = readStoredUser();
      const data = dedupeNotifications(parseNotificationsResponse(res))
        .filter((notification) => isLicensedNotification(user, notification));
      setStore({ notifications: data, loading: false });
    } catch (err) {
      console.error('Failed to load notifications', err);
      setStore({ loading: false });
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

const addSocketNotification = (payload) => {
  const normalized = normalizeSocketNotification(payload);
  if (!normalized) return;
  if (!isLicensedNotification(readStoredUser(), normalized)) return;

  const key = notificationKey(normalized);
  if (key === lastProcessedSocketKey) return;
  lastProcessedSocketKey = key;

  setStore({
    notifications: dedupeNotifications([{ ...normalized, read: normalized.read ?? false }, ...store.notifications]),
  });
};

const markNotificationRead = (n) => {
  if (!isUnread(n)) return;
  setStore({
    notifications: store.notifications.map((item) =>
      notificationKey(item) === notificationKey(n) ? { ...item, read: true } : item
    ),
  });
};

export function useNotifications({ autoLoad = true } = {}) {
  const { notification } = useSelector((state) => state.socket);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { notifications, loading, unreadCount } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!autoLoad) return;
    fetchNotifications();
  }, [autoLoad]);

  useEffect(() => {
    if (!notification) return;
    addSocketNotification(notification);
  }, [notification]);

  const reload = useCallback(() => fetchNotifications(), []);

  const openNotification = useCallback(
    (n) => {
      dispatch(readNotificationSocket(n));
      markNotificationRead(n);
      // ForeSight warning notifications deep-link to the warning's own page.
      if (n?.warning_id && ['EARLY_WARNING', 'EARLY_WARNING_DISPATCH'].includes(n?.type)) {
        navigate(`/foresight/warnings/${n.warning_id}`);
        return;
      }
      navigate(`/incident-details/${n.id}`);
    },
    [dispatch, navigate]
  );

  return { notifications, unreadCount, loading, openNotification, reload };
}
